import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getRestaurantBySlug, getMenuItems } from '../services/firebaseService';
import { trackPageView } from '../services/analyticsService'; // Import analytics function
import { getCurrencySymbol } from '../config/currencies'; // Import currency helper
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import './MenuPage.css';
import LoadingSpinner from '../components/loadingspinner';// Import the CSS file

function MenuPage() {
  const { slug } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState(['All']);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [selectedPortions, setSelectedPortions] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const restaurantData = await getRestaurantBySlug(slug);
        
        if (!restaurantData) {
          setError('Restaurant not found');
          setLoading(false);
          return;
        }
        setRestaurant(restaurantData); // Set the full restaurant object
        trackPageView(`MenuPage - ${restaurantData.name || slug}`, window.location.pathname + window.location.search, restaurantData.id);

        // Check if the restaurant is active
        if (restaurantData.isActive === false) { // Explicitly check for false
          setError('This restaurant is currently unavailable.');
          setLoading(false);
          return;
        }
        
        // Check if the restaurant is closed
        if (restaurantData.isClosed === true) {
          setError('This restaurant is currently closed.');
          setLoading(false);
          return;
        }
        
        // Check if menu access is enabled
        if (restaurantData.menuEnabled === false) { // Explicitly check if menu is disabled
          setError('Menu access is currently disabled for this restaurant.');
          setLoading(false);
          return;
        }
        
        // First, check for active menus in the new menu structure
        let items = [];
        
        try {
          console.log(`MenuPage: Checking for active menus for restaurant ${restaurantData.id}`);
          const menusRef = collection(db, 'restaurants', restaurantData.id, 'menus');
          const activeMenuQuery = query(menusRef, where('isActive', '==', true), orderBy('createdAt', 'desc'));
          const activeMenuSnap = await getDocs(activeMenuQuery);
          
          console.log(`MenuPage: Found ${activeMenuSnap.size} active menus`);
          
          if (!activeMenuSnap.empty) {
            // If there are active menus, fetch items from all of them
            console.log(`MenuPage: Using ${activeMenuSnap.size} active menus`);
            
            for (const activeMenuDoc of activeMenuSnap.docs) {
              const activeMenu = activeMenuDoc;
              console.log(`MenuPage: Fetching items from menu: ${activeMenu.data().name} (ID: ${activeMenu.id})`);
              
              const menuItemsRef = collection(db, 'restaurants', restaurantData.id, 'menus', activeMenu.id, 'menuItems');
              const menuItemsSnap = await getDocs(menuItemsRef);
              
              const menuItems = menuItemsSnap.docs.map(doc => ({
                id: doc.id,
                menuId: activeMenu.id,
                menuName: activeMenu.data().name,
                ...doc.data()
              }));
              
              items = items.concat(menuItems);
              console.log(`MenuPage: Loaded ${menuItems.length} items from menu: ${activeMenu.data().name}`);
            }
            
            console.log(`MenuPage: Total loaded ${items.length} items from all active menus`);
          } else {
            // Fallback to legacy menu items if no active menu exists
            console.log(`MenuPage: No active menus found, falling back to legacy menu`);
            items = await getMenuItems(restaurantData.id);
            console.log(`MenuPage: Loaded ${items.length} items from legacy menu`);
          }
        } catch (menuError) {
          console.error('MenuPage: Error fetching active menus, falling back to legacy:', menuError);
          // If there's any error with the new menu structure, fall back to legacy
          try {
            items = await getMenuItems(restaurantData.id);
            console.log(`MenuPage: Fallback successful - loaded ${items.length} items from legacy menu`);
          } catch (legacyError) {
            console.error('MenuPage: Error fetching legacy menu items:', legacyError);
            throw legacyError; // Re-throw if even legacy fails
          }
        }
        
        // Include all items, don't filter out hidden ones
        setMenuItems(items);
        
        // Initialize selectedPortions for portion-wise items
        const initialPortions = {};
        items.forEach(item => {
          if (item.isPortionWise && item.portions && item.portions.length > 0) {
            initialPortions[item.id] = item.portions[0].name; // Default to first portion
          }
        });
        setSelectedPortions(initialPortions);
        
        const uniqueCategories = [...new Set(items.map(item => item.category))];
        setCategories(['All', ...uniqueCategories]);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load menu data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [slug]);

  // useEffect for page view tracking was moved into the main fetchData useEffect
  /*
  useEffect(() => {
    // This useEffect was for page view, but it's better to track after restaurant data is fetched
    // to include restaurant ID.
  }, [slug, restaurant]); // Ensure restaurant is a dependency if used here
  */

  const handleImageClick = (item) => {
    setModalItem(item);
    setShowImageModal(true);
  };

  const closeModal = () => {
    setShowImageModal(false);
    setModalItem(null);
  };

  // Helper function to handle portion selection change
  const handlePortionChange = (itemId, portion) => {
    setSelectedPortions(prev => ({
      ...prev,
      [itemId]: portion
    }));
  };

  // Helper function to get display price based on selected portion
  const getDisplayPrice = (item) => {
    if (!item.isPortionWise) {
      return item.price;
    }
    
    if (!item.portions || item.portions.length === 0) {
      return item.price; // Fallback to regular price if no portions
    }
    
    const selectedPortion = selectedPortions[item.id] || item.portions[0].name;
    const portion = item.portions.find(p => p.name === selectedPortion);
    return portion ? portion.price : item.portions[0].price;
  };

  // Filter menu items by category and search term
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  // Define Modal Component within MenuPage scope
  const ImageModal = ({ item, onClose }) => {
    if (!item) return null;
  
    return (
      <div className="image-modal-overlay" onClick={onClose}>
        <div className="image-modal-content" onClick={(e) => e.stopPropagation()}> 
          {item.photoUrl ? (
            <img src={item.photoUrl} alt={item.name} />
          ) : (
            <div className="modal-image-placeholder">
              <i className="fas fa-utensils"></i>
              <span>No Image Available</span>
            </div>
          )}
          <div className="image-modal-info">
            <h3 className="modal-item-name">{item.name}</h3>
            {item.description && (
              <p className="modal-item-description">{item.description}</p>
            )}
            <div className="modal-item-price">
              {item.offerPrice && item.offerPrice < item.price ? (
                <>
                  <span className="modal-offer-price">
                    {getCurrencySymbol(restaurant?.currency)}{item.offerPrice}
                  </span>
                  <span className="modal-original-price">
                    {getCurrencySymbol(restaurant?.currency)}{item.price}
                  </span>
                </>
              ) : (
                <span className="modal-regular-price">
                  {getCurrencySymbol(restaurant?.currency)}{item.price}
                </span>
              )}
            </div>
            {item.prepTime && (
              <div className="modal-prep-time">
                <i className="far fa-clock"></i>
                {item.prepTime} min
              </div>
            )}
          </div>
          <button className="image-modal-close" onClick={onClose}>&times;</button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    // Check if it's a restaurant closure message
    const isClosedMessage = error.includes('closed');
    
    return (
      <div className="container centered">
        <div className={`error-container ${isClosedMessage ? 'restaurant-closed' : ''}`}>
          {isClosedMessage ? (
            <>
              <div className="closed-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="64" height="64" fill="#e74c3c">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"/>
                </svg>
              </div>
              <h2 className="closed-title">This Restaurant is Currently Closed</h2>
              <p className="closed-message">We're sorry, but this restaurant is not accepting orders at this time.</p>
              <p className="closed-hours">Please check back later during business hours.</p>
            </>
          ) : (
            <p className="error-message">{error}</p>
          )}
          <Link to="/" className="btn mt-4">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
    <div className="header" style={{
      position: 'relative',
      width: '100%'
    }}>
      
      <div className="header-content" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}>
        {/* Restaurant Logo - Full Width */}
        {restaurant?.logoUrl && (
          <div style={{
            width: '100%',
          }}>
            <img 
              src={restaurant.logoUrl} 
              alt={`${restaurant.name} Logo`} 
              style={{ 
                width: '100%',
                height: 'auto',
                maxHeight: '150px',
                objectFit: 'cover'
              }} 
            />
          </div>
        )}
        
        <h1 style={{ 
          textAlign: 'center',
          margin: '0',
          fontSize: '2rem',
          fontWeight: 'bold',
          width: '100%'
        }}>{restaurant?.name}</h1>
      </div>
    </div>
      <div className="search-bar">
        <i className="fas fa-search"></i>
        <input
          type="text"
          placeholder="Search menu items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      <div className="category-tabs">
        {categories.map(category => (
          <div
            key={category}
            className={`category-tab ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </div>
        ))}
      </div>
      
      {filteredItems.length === 0 ? (
        <div className="centered mt-4">
          <p>No menu items found.</p>
        </div>
      ) : (
        <div className="menu-grid">
          {filteredItems.map(item => (
            <div key={item.id} className={`menu-item ${item.isHidden ? 'menu-item-disabled' : ''}`}>
              {item.isHidden && (
                <div className="out-of-stock-overlay">
                  <span>Out of Stock</span>
                </div>
              )}
              {item.photoUrl ? (
                <div className="menu-item-image-container" onClick={!item.isHidden ? () => handleImageClick(item) : undefined} style={{ cursor: item.isHidden ? 'default' : 'pointer' }}>
                  <img src={item.photoUrl} alt={item.name} />
                </div>
              ) : (
                <div className="menu-item-placeholder"></div>
              )}
              
              {item.offerPrice && item.offerPrice < item.price && !item.isHidden && (
                <div className="offer-badge" >Offer</div>
              )}
              
              <div className="menu-item-content" style={{marginBottom:10}}>
                <div className="menu-item-category">
                <h3 className="menu-item-title">{item.name}</h3>
                </div>
               
                {item.description && (
                  <p className="menu-item-description">{item.description}</p>
                )}
                
                {/* Portion Selection Radio Buttons - Only for portion-wise items */}
                {item.isPortionWise && item.portions && item.portions.length > 0 && (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      gap: '15px', 
                      marginBottom: '10px', 
                      justifyContent: 'left', 
                      flexWrap: 'wrap' 
                    }}> 
                      {item.portions.map(portion => ( 
                        <label key={portion.id} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          cursor: 'pointer', 
                          fontSize: '0.9rem', 
                          fontWeight: selectedPortions[item.id] === portion.name ? 'bold' : 'normal', 
                          color: selectedPortions[item.id] === portion.name ? '#2563eb' : '#666'
                        }}> 
                          <input 
                            type="radio" 
                            name={`portion-${item.id}`} 
                            value={portion.name} 
                            checked={selectedPortions[item.id] === portion.name} 
                            onChange={() => handlePortionChange(item.id, portion.name)} 
                            style={{ marginRight: '5px' }} 
                          /> 
                          {portion.name}
                        </label> 
                      ))} 
                    </div>
                    
                    {/* Show description only for selected portion */}
                    {(() => {
                      const selectedPortion = selectedPortions[item.id] || item.portions[0].name;
                      const portion = item.portions.find(p => p.name === selectedPortion);
                      return portion && portion.description ? (
                        <div style={{ 
                          fontSize: '0.85rem', 
                          color: '#666', 
                          marginBottom: '10px',
                          lineHeight: '1.3',
                          fontStyle: 'italic',
                          padding: '8px 12px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          {portion.description}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
                
                <div className="menu-item-footer">
                  <div className="menu-item-price">
                  {item.offerPrice && item.offerPrice < item.price && !item.isPortionWise ? (
                    <>
                         {/* Offer price - displayed prominently */}
                         <span style={{ 
                        fontSize: '1rem',  // Make offer price slightly larger
                        fontWeight: 'bold',  // Make offer price bold
                        color: '#2563eb'     // Optional: make offer price blue to highlight savings
                        }}>
                        {getCurrencySymbol(restaurant?.currency)}{item.offerPrice}
                        </span>

                        {/* Original price - displayed smaller with strikethrough */}
                        <span style={{ 
                        textDecoration: 'line-through', 
                        color: '#888', 
                        marginLeft: '8px',  // Changed to marginLeft for better spacing
                        fontSize: '0.8rem'  // Make original price smaller
                        }}>
                        {getCurrencySymbol(restaurant?.currency)}{" "}{item.price}
                        </span>
                        

                    </>
                    ) : (
                    // If no offer price or portion-wise item, show appropriate price
                    <span style={{ fontWeight: 'bold' }}>
                        {getCurrencySymbol(restaurant?.currency)}{" "}{getDisplayPrice(item)}
                    </span>
                    )}
                  </div>
                  {item.prepTime && (
                    <div className="menu-item-prep" >
                      <i className="far fa-clock"></i>
                      {item.prepTime} min
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Render Modal conditionally */}
      {showImageModal && <ImageModal item={modalItem} onClose={closeModal} />}
    </div>
  );
}

export default MenuPage;